import debug from "@cher-ami/debug"
import { Component } from "~/libs/compose"
import PostVoteDataViz from "../postVoteDataViz/PostVoteDataViz"
import mitt, { Emitter } from "mitt"
import postsNavigationService from "~/services/PostsNavigationService"

const componentName = "PostSlide"
const log = debug(`front:${componentName}`)

type TStaticProps = {}

export type PostSlideEvents = {
	"article:resize": { width: number; height: number }
	"content:loaded": boolean
}

/**
 * @name PostSlide
 */
export default class PostSlide extends Component<TStaticProps> {
	static attrName = componentName

	public emitter: Emitter<PostSlideEvents> = mitt<PostSlideEvents>()

	public articleBodyResizeObserver: ResizeObserver
	public isCurrent: boolean

	public dataviz = this.add(PostVoteDataViz)

	public elements = {
		$content: this.find<HTMLDivElement>("content"),
		$contentPlaceholder: this.$root.querySelector<HTMLElement>(
			"[data-content-placeholder]"
		)
	}
	public url: string
	public logoSrc: string
	public postId: string
	public isContentLoaded: boolean

	// ----------------------------------------------------------------------------- LIFECYCLE

	mounted() {
		// // box width is the width of the slide
		// this.boxWidth = this.elements.$slides[0].offsetWidth
		// this.translateXOffset = this.boxWidth + this.gap
		// this.initSlider()
		this.url = this.$root.dataset.url
		this.logoSrc = this.$root.dataset.logoSrc
		this.postId = this.$root.dataset.postId
		this.isContentLoaded = this.$root.dataset.contentLoaded === "true"
		this.attachEvents()

		log("> mounted: " + this.url)
		log("> dataviz of " + this.postId + " is loaded: " + this.dataviz)
	}

	unmounted() {
		this.detachEvents()
	}

	// ----------------------------------------------------------------------------- EVENTS

	attachEvents() {
		//this.elements.$buttonNext.addEventListener("click", this.next.bind(this))
		// this.elements.$buttonPrev.addEventListener("click", this.prev.bind(this))
		const link = this.$root.querySelector(".post-article__push-link")
		if (link) {
			link.addEventListener("click", this.onClickNextLink.bind(this))
		}

		// TODO: check for only current slide and not all slides
		//  resize observer for .post-article
		this.articleBodyResizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const { width, height } = entry.contentRect
				this.emitter.emit("article:resize", { width, height })
			}
		})
		this.articleBodyResizeObserver.observe(this.$root)
	}

	detachEvents() {
		// this.elements.$buttonNext.removeEventListener("click", this.next.bind(this))
		// this.elements.$buttonPrev.removeEventListener("click", this.prev.bind(this))

		const link = this.$root.querySelector(".post-article__push-link")
		if (link) {
			link.removeEventListener("click", this.onClickNextLink.bind(this))
		}

		// remove resize observer
		this.articleBodyResizeObserver.disconnect()
	}

	onClickNextLink(e: MouseEvent) {
		e.stopPropagation()
		e.preventDefault()

		const nextIndex =
			(postsNavigationService.currentIndex + 1) % postsNavigationService.totalPosts
		postsNavigationService.updateCurrentPost(nextIndex)
	}

	// ----------------------------------------------------------------------------- CONTENT LOADING

	/**
	 * Load content for this slide if not already loaded
	 */
	async loadContent(): Promise<boolean> {
		if (this.$root.dataset.contentLoaded === "true" || this.isContentLoaded) return true
		// Get postId from dataset if this.postId is undefined
		const postId = this.postId || this.$root.dataset.postId
		if (!postId) {
			console.warn("Cannot load content: missing postId for slide", this.$root)
			return false
		}
		// Ensure this.postId is set for future calls
		this.postId = postId

		try {
			const response = await fetch(`/api/post-content/${this.postId}.json`)

			if (!response.ok) throw new Error(`Failed to load content: ${response.statusText}`)

			const data = await response.json()

			const placeholder = this.$root.parentElement.querySelector(
				'.content-placeholder[data-post-id="' + this.postId + '"]'
			)
			if (placeholder && data.content) {
				placeholder.innerHTML = data.content
				log("> load content of" + this.postId)
				this.isContentLoaded = true
				this.$root.dataset.contentLoaded = "true"
				// TODO: remove if not needed
				this.emitter.emit("content:loaded", true)

				// Reinitialize dataviz component if it's undefined
				if (!this.dataviz) {
					this.dataviz = this.add(PostVoteDataViz)
					log("> reinit dataviz of " + this.postId)
				}

				return true
			} else {
				console.warn("Failed to load content: placeholder or content not found", {
					placeholder,
					postId: this.postId,
					dataContent: !!data.content
				})
				return false
			}
		} catch (error) {
			console.error("Error loading content:", error)
			return false
		}
	}

	/**
	 * Check if slide should have content loaded based on navigation
	 * @param currentIndex The current active slide index
	 */
	shouldLoadContent(currentIndex: number): boolean {
		if (this.isContentLoaded) return false

		const slideIndex = parseInt(this.$root.dataset.slideIndex, 10)
		const totalSlides = postsNavigationService.totalPosts

		// Load current slide, previous and next slides
		return (
			slideIndex === currentIndex ||
			slideIndex === (currentIndex - 1 + totalSlides) % totalSlides ||
			slideIndex === (currentIndex + 1) % totalSlides
		)
	}

	// ----------------------------------------------------------------------------- ANIMATION

	override playIn(delay): void {}
}
